import { __ } from "@wordpress/i18n";
import * as Yup from "yup";
import { validationsLocale } from "./ValidationsLocale";

// Set localized validation messages
Yup.setLocale(validationsLocale);

/**
 * Constants matching the backend FormRequest validation limits
 */
const VALIDATION_LIMITS = {
  MIN_INCOME: 0.01,
  MAX_INCOME: 999999.99,
  MIN_EXPENSE: 0,
  MAX_EXPENSE: 999999.99,
} as const;

/**
 * Form validation schemas for different simulator types
 * Uses Yup to define validation rules and error messages for each field
 *
 * IMPORTANT: Only net_monthly_income is required, all other fields are optional
 * to maintain compatibility with backend FormRequest validation.
 * However, we validate that at least one expense field has a value for meaningful calculation.
 */
export const validations: Record<string, Yup.ObjectSchema<any>> = {
  /**
   * Validation schema for Effort Rate calculator
   * Defines rules for all form fields - only income is required,
   * but at least one expense must be provided
   */
  effort_rate: Yup.object()
    .shape({
      // Net monthly income - REQUIRED positive number
      net_monthly_income: Yup.number()
        .typeError(
          __("O rendimento mensal líquido deve ser um número", "df-simulators"),
        )
        .min(
          VALIDATION_LIMITS.MIN_INCOME,
          __(
            "O rendimento mensal líquido deve ser maior que zero",
            "df-simulators",
          ),
        )
        .max(
          VALIDATION_LIMITS.MAX_INCOME,
          __(
            "O rendimento mensal líquido não pode exceder €999.999,99",
            "df-simulators",
          ),
        )
        .required(
          __("O rendimento mensal líquido é obrigatório", "df-simulators"),
        ),

      // Mortgage - OPTIONAL non-negative number
      mortgage: Yup.number()
        .typeError(
          __("O crédito habitação deve ser um número", "df-simulators"),
        )
        .min(
          VALIDATION_LIMITS.MIN_EXPENSE,
          __(
            "O crédito habitação deve ser maior ou igual a zero",
            "df-simulators",
          ),
        )
        .max(
          VALIDATION_LIMITS.MAX_EXPENSE,
          __(
            "O crédito habitação não pode exceder €999.999,99",
            "df-simulators",
          ),
        )
        .nullable()
        .transform((value, originalValue) => {
          return originalValue === "" ? null : value;
        }),

      // Car loan - OPTIONAL non-negative number
      car_loan: Yup.number()
        .typeError(
          __("O crédito automóvel deve ser um número", "df-simulators"),
        )
        .min(
          VALIDATION_LIMITS.MIN_EXPENSE,
          __(
            "O crédito automóvel deve ser maior ou igual a zero",
            "df-simulators",
          ),
        )
        .max(
          VALIDATION_LIMITS.MAX_EXPENSE,
          __(
            "O crédito automóvel não pode exceder €999.999,99",
            "df-simulators",
          ),
        )
        .nullable()
        .transform((value, originalValue) => {
          return originalValue === "" ? null : value;
        }),

      // Personal loan - OPTIONAL non-negative number
      personal_loan: Yup.number()
        .typeError(__("O crédito pessoal deve ser um número", "df-simulators"))
        .min(
          VALIDATION_LIMITS.MIN_EXPENSE,
          __(
            "O crédito pessoal deve ser maior ou igual a zero",
            "df-simulators",
          ),
        )
        .max(
          VALIDATION_LIMITS.MAX_EXPENSE,
          __("O crédito pessoal não pode exceder €999.999,99", "df-simulators"),
        )
        .nullable()
        .transform((value, originalValue) => {
          return originalValue === "" ? null : value;
        }),

      // Credit card debt - OPTIONAL non-negative number
      credit_card_debt: Yup.number()
        .typeError(
          __(
            "A dívida do cartão de crédito deve ser um número",
            "df-simulators",
          ),
        )
        .min(
          VALIDATION_LIMITS.MIN_EXPENSE,
          __(
            "A dívida do cartão de crédito deve ser maior ou igual a zero",
            "df-simulators",
          ),
        )
        .max(
          VALIDATION_LIMITS.MAX_EXPENSE,
          __(
            "A dívida do cartão de crédito não pode exceder €999.999,99",
            "df-simulators",
          ),
        )
        .nullable()
        .transform((value, originalValue) => {
          return originalValue === "" ? null : value;
        }),

      // Other loans - OPTIONAL non-negative number
      other_loans: Yup.number()
        .typeError(__("Outros créditos deve ser um número", "df-simulators"))
        .min(
          VALIDATION_LIMITS.MIN_EXPENSE,
          __("Outros créditos deve ser maior ou igual a zero", "df-simulators"),
        )
        .max(
          VALIDATION_LIMITS.MAX_EXPENSE,
          __("Outros créditos não pode exceder €999.999,99", "df-simulators"),
        )
        .nullable()
        .transform((value, originalValue) => {
          return originalValue === "" ? null : value;
        }),

      // Total domestic burdens - OPTIONAL non-negative number (informational only)
      /*
      total_domestic_burdens: Yup.number()
        .typeError(
          __("Os encargos domésticos devem ser um número", "df-simulators"),
        )
        .min(
          VALIDATION_LIMITS.MIN_EXPENSE,
          __(
            "Os encargos domésticos devem ser maior ou igual a zero",
            "df-simulators",
          ),
        )
        .max(
          VALIDATION_LIMITS.MAX_EXPENSE,
          __(
            "Os encargos domésticos não podem exceder €999.999,99",
            "df-simulators",
          ),
        )
        .nullable()
        .transform((value, originalValue) => {
          return originalValue === "" ? null : value;
        }),
      */

      // Life insurance - OPTIONAL non-negative number (informational only)
      /*
      life_insurance: Yup.number()
        .typeError(__("O seguro de vida deve ser um número", "df-simulators"))
        .min(
          VALIDATION_LIMITS.MIN_EXPENSE,
          __(
            "O seguro de vida deve ser maior ou igual a zero",
            "df-simulators",
          ),
        )
        .max(
          VALIDATION_LIMITS.MAX_EXPENSE,
          __("O seguro de vida não pode exceder €999.999,99", "df-simulators"),
        )
        .nullable()
        .transform((value, originalValue) => {
          return originalValue === "" ? null : value;
        }),
      */

      // Multi-risk insurance - OPTIONAL non-negative number (informational only)
      /*
      multi_risk_insurance: Yup.number()
        .typeError(
          __("O seguro multiriscos deve ser um número", "df-simulators"),
        )
        .min(
          VALIDATION_LIMITS.MIN_EXPENSE,
          __(
            "O seguro multiriscos deve ser maior ou igual a zero",
            "df-simulators",
          ),
        )
        .max(
          VALIDATION_LIMITS.MAX_EXPENSE,
          __(
            "O seguro multiriscos não pode exceder €999.999,99",
            "df-simulators",
          ),
        )
        .nullable()
        .transform((value, originalValue) => {
          return originalValue === "" ? null : value;
        }),
        */
    })
    .test(
      "at-least-one-expense",
      __(
        "Para calcular a taxa de esforço, deve preencher pelo menos uma despesa além do rendimento.",
        "df-simulators",
      ),
      function (values) {
        // List of expense fields to check
        const expenseFields = [
          "mortgage",
          "car_loan",
          "personal_loan",
          "credit_card_debt",
          "other_loans",
          /*"total_domestic_burdens",
          "life_insurance",
          "multi_risk_insurance",*/
        ];

        // Check if at least one expense field has a value greater than 0
        const hasAtLeastOneExpense = expenseFields.some((field) => {
          const value = values[field];
          return (
            value !== null &&
            value !== undefined &&
            value !== "" &&
            Number(value) > 0
          );
        });

        if (!hasAtLeastOneExpense) {
          // Return error at the form level, not at specific field level
          return this.createError({
            path: "custom-error",
            message: __(
              "Para calcular a taxa de esforço, deve preencher pelo menos uma despesa além do rendimento.",
              "df-simulators",
            ),
          });
        }

        return true;
      },
    )
    .test(
      "expenses-exceed-income-limit",
      __(
        "As despesas mensais não podem ser superiores a três vezes o rendimento mensal líquido.",
        "df-simulators",
      ),
      function (values) {
        // Mirrors the backend rule: Total Expenses > Income * 3
        const expenseFields = [
          "mortgage",
          "car_loan",
          "personal_loan",
          "credit_card_debt",
          "other_loans",
          /*"total_domestic_burdens",
          "life_insurance",
          "multi_risk_insurance",*/
        ];

        const income = Number(values.net_monthly_income);
        if (!income || income <= 0) {
          // Income itself is invalid/empty - its own validation already covers this.
          return true;
        }

        const totalExpenses = expenseFields.reduce((sum, field) => {
          const value = values[field];
          const hasValue = value !== null && value !== undefined && value !== "";
          return sum + (hasValue ? Number(value) : 0);
        }, 0);

        if (totalExpenses > income * 3) {
          // Surface the error on the income field, as required by the UX.
          return this.createError({
            path: "net_monthly_income",
            message: __(
              "As despesas mensais não podem ser superiores a três vezes o rendimento mensal líquido.",
              "df-simulators",
            ),
          });
        }

        return true;
      },
    ),
};
